import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Flex,
  useDisclosure,
  VStack,
  HStack,
  Icon,
  IconButton,
  Text,
  Input,
  Tooltip,
  Button,
  CheckboxGroup,
  Stack,
  Checkbox,
  Link,
  CloseButton,
  useColorModeValue,
  Textarea,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  DeleteIcon,
  AttachmentIcon,
  CheckIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileSignature, faDollarSign, faBookmark } from "@fortawesome/free-solid-svg-icons";
import { RiSparkling2Fill } from "react-icons/ri";
import { cqAPI, getBid } from "../../lib/Auth";
import creditCardModalManager from "../DialogManager/creditCardModalManager";
import { sendEvent } from "../../lib/GA";
import MessagesTemplatesModal from "./MessageTemplatesModals";
const _ = require("lodash");

// Looks like a single line until Enter; auto-grows; keeps focus (uncontrolled)
const AutoGrowUncontrolledTextarea = React.memo(function AutoGrowUncontrolledTextarea({
  name,
  initialValue = "",
  placeholder,
  onValueChange, // (val: string) => void
}) {
  const ref = React.useRef(null);

  const handleInput = (e) => {
    const el = e.currentTarget;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
    onValueChange?.(el.value);
  };

  React.useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = ref.current.scrollHeight + "px";
  }, []);

  return (
    <Textarea
      name={name}
      ref={ref}
      defaultValue={initialValue}
      placeholder={placeholder}
      onInput={handleInput}
      rows={1}
      resize="none"
      overflow="hidden"
      variant="outline"
      size="md"
    />
  );
});

function SendArea({
  isPatient,
  disableSendButton,
  toast,
  messageType,
  setMessageType,
  typing,
  colorMode,
  uid,
  isStripeConnected,
  setDisableSendButton,
  staff,
  draft,
  setDraft,
  userInfo,
  isDraftBoxOpen,
  isMobile,
  onSubmit, // optional callback: (body, attachments) => Promise<response>
}) {
  const [filteredStaff, setFilteredStaff] = useState([]);
  const fileInputRef = useRef(null);

  // Replacements modal
  const {
    isOpen: isReplacementsOpen,
    onOpen: onReplacementsOpen,
    onClose: onReplacementsClose,
  } = useDisclosure();

  const {
    isOpen: isMessageTemplatesOpen,
    onOpen: onMessageTemplatesOpen,
    onClose: onMessageTemplatesClose,
  } = useDisclosure();

  const [payRequest, setPayRequest] = useState({});
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [attachedPayRequest, setAttachedPayRequest] = useState({});
  const [selectedForm, setSelectedForm] = useState("");
  const [attachedForm, setAttachedForm] = useState(null);

  // Track replacement values
  const [replacementValues, setReplacementValues] = useState({}); // scratch in modal
  const [attachedFormReplacements, setAttachedFormReplacements] = useState(null); // persisted with the attached form

  const textareaRef = useRef(null);
  const [placeHolderVisible, setPlaceHolderVisible] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHAR_COUNT = 1600;
  const iconBGColor = useColorModeValue("#F9F9FB", "#2C2F40");

  // Translation toggle
  const [translateEnabled, setTranslateEnabled] = useState(false);
  const [translationLangCode, setTranslationLangCode] = useState("");

  const [mentionedStaff, setMentionedStaff] = useState([]);
  const [replyType, setReplyType] = useState("reply");

  // Avoid race with contentEditable initial fill
  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const messageParam = searchParams.get("message");
    if (!messageParam) return;

    let preFilled = "";
    try {
      preFilled = decodeURIComponent(messageParam);
    } catch {
      // ignore
    }

    if (preFilled && textareaRef.current) {
      textareaRef.current.innerText = preFilled;
      setCharCount(preFilled.length);
      setPlaceHolderVisible(false);
      adjustTextareaHeight();
    }
  }, []);

  const handlePaste = (event) => {
    event.preventDefault();
    const pastingText = event.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    const textNode = document.createTextNode(pastingText);
    range.deleteContents();
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
    const currentValue = textareaRef.current.innerText;
    setCharCount(currentValue.length);
  };

  const handlePlaceHolderClick = () => {
    textareaRef.current.focus();
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    }
  };

  const handleFocus = () => {
    setPlaceHolderVisible(false);
  };

  const setCursorAfterHtmlInsert = (contentEle) => {
    const range = document.createRange();
    const selection = window.getSelection();
    range.setStart(contentEle, contentEle.childNodes.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const debouncedTyping = _.debounce(async () => {
    await cqAPI.post("/chat/typing", {
      bid: getBid(),
      target_uid: userInfo.user_id,
      is_internal_note: replyType === "internalNote",
    });
  }, 3000);

  const insertStaffNameAtCursor = (member) => {
    setMentionedStaff([...mentionedStaff, member]);
    const currentValue = textareaRef.current.innerHTML;
    const lastAtPos = currentValue.lastIndexOf("@");
    const beforeAt = currentValue.substring(0, lastAtPos);
    textareaRef.current.innerHTML = `${beforeAt}<b>${member.profile.name}</b>&nbsp;`;
    setCursorAfterHtmlInsert(textareaRef.current);
    setFilteredStaff([]);
  };

  const deleteStaff = (member) => {
    textareaRef.current.innerHTML = textareaRef.current.innerHTML.replace(
      `<b>${member.profile.name}</b>`,
      ""
    );
    setCursorAfterHtmlInsert(textareaRef.current);
    setMentionedStaff([...mentionedStaff.filter((s) => s._id !== member._id)]);
  };

  const checkMention = async function () {
    await debouncedTyping();
    const currentValue = textareaRef.current.innerText;
    const lastAtPos = currentValue.lastIndexOf("@");

    if (lastAtPos !== -1 && /\@[a-zA-Z ]{1,14}$/.test(currentValue)) {
      const searchQuery = currentValue.substring(lastAtPos + 1).toLowerCase();
      const matchingStaff = staff
        .filter((s) => {
          const nameParts = s.profile.name.toLowerCase().split(" ");
          return nameParts.some((part) => part.startsWith(searchQuery));
        })
        .filter((s) => !mentionedStaff.find((ss) => ss._id === s._id))
        .slice(0, 3);
      if (!_.isEqual(matchingStaff, filteredStaff)) {
        setFilteredStaff(matchingStaff);
      }
    } else {
      setFilteredStaff([]);
    }
  };

  const handleBlur = () => {
    if (textareaRef.current.innerText) return;
    setPlaceHolderVisible(true);
  };

  const handleInput = () => {
    adjustTextareaHeight();
  };

  const handleDeleteAttachment = (index) => {
    setAttachedFiles((currentFiles) => currentFiles.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    const uploadedFiles = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append("file", file, encodeURIComponent(file.name));
      try {
        const response = await cqAPI.post("/upload", formData);
        if (response.type === "ok") {
          uploadedFiles.push({
            name: file.name || "Attachment",
            url: response.url,
            mimetype: file.type,
          });
        } else {
          toast({
            title: "Error uploading file.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          break;
        }
      } catch (error) {
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        break;
      }
    }
    if (uploadedFiles.length > 0) {
      toast({
        title: "File(s) uploaded successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      setAttachedFiles((currentFiles) => [...currentFiles, ...uploadedFiles]);
      setFileInputKey(Date.now());
    }
  };


  const handleFileButtonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSubmit = async () => {
    if (!textareaRef.current.innerText && !draft) {
      toast({
        title: "The Message Cannot be Empty!",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    setDisableSendButton(true);

    const msgBody = textareaRef.current.innerText
      ? textareaRef.current.innerText
      : draft
      ? draft
      : "";

    // Only include non-empty replacements; omit entirely if none
    const nonEmptyReplacements =
      attachedFormReplacements &&
      Object.fromEntries(
        Object.entries(attachedFormReplacements).filter(
          ([, v]) => typeof v === "string" && v.trim().length > 0
        )
      );
    const includeReplacements =
      nonEmptyReplacements && Object.keys(nonEmptyReplacements).length > 0;

    let response;
    if (onSubmit) {
      response = await onSubmit(msgBody, attachedFiles, mentionedStaff.map(v => v._id));
    } else {
      response = await cqAPI.post("/messages", {
        body: msgBody,
        to: uid,
        type : 'intercom',
        attachments: attachedFiles
      });
    }

    setDisableSendButton(false);

    if (response.type === "ok") {

      // Clear states
      setCharCount(0);
      setAttachedFiles([]);
      setPayRequest({});
      setAttachedPayRequest({});
      setAttachedForm(null);
      setAttachedFormReplacements(null);
      textareaRef.current.innerText = "";
      setMentionedStaff([]);

      const url = new URL(window.location.href);
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
    } else if (response.msg) {
      toast({
        title: "Error sending the message.",
        description: response.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const replacementKeys = React.useMemo(
    () => attachedForm?.replacements ?? [],
    [attachedForm]
  );

  return (
    <>
      <Box
        w="100%"
        className="input-area"
        bg={
          replyType === "internalNote"
            ? "orange.100"
            : colorMode === "dark"
            ? "#212330"
            : "#FFFFFF"
        }
        color={
          replyType === "internalNote"
            ? "#000000"
            : colorMode === "light"
            ? "#000000"
            : "#ffffff"
        }
      >
        <Flex direction="column" flex="1">
          <VStack spacing={3} align="stretch">
            {/* Hidden file input */}
            <Input
              ref={fileInputRef}
              key={fileInputKey}
              type="file"
              onChange={handleFileUpload}
              hidden
            />

            {/* Tools row */}
            <HStack
              className="stack_custom_style"
              style={{ zIndex: 99999, width: "100%", justifyContent: "space-between" }}
            >
              <HStack className="chatTools">
                <>
                  <IconButton
                    icon={<AttachmentIcon />}
                    id={"attach-icon"}
                    onClick={handleFileButtonClick}
                    isDisabled={disableSendButton}
                    variant="none"
                    bg={iconBGColor}
                    className={replyType === "internalNote" ? "attachmentIcon" : ""}
                    mr={1}
                  />
                </>

                {/* Staff mention area */}
                <Stack direction="row" spacing={4} mr={2}>
                  <Flex className="staff-selector">
                    {mentionedStaff.length > 0 &&
                      mentionedStaff.map((member) => (
                        <div key={member._id} className="staff-member">
                          {member.profile.name}
                          <IconButton
                            icon={<DeleteIcon />}
                            onClick={() => deleteStaff(member)}
                            variant="none"
                            size="xs"
                            style={{
                              filter:
                                "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                            }}
                          />
                        </div>
                      ))}
                    {filteredStaff.length > 0 &&
                      filteredStaff.map((member) => (
                        <div
                          key={member._id}
                          onClick={() => insertStaffNameAtCursor(member)}
                          className="staff-member"
                        >
                          {member.profile.name}
                        </div>
                      ))}
                  </Flex>
                </Stack>

                {attachedFiles && (
                  <Flex justify="flex-end">
                    {attachedFiles.map((attachedFile, index) => (
                      <Flex key={index} position="relative" ml={index > 0 ? 2 : 0}>
                        <Tooltip label={attachedFile.name} hasArrow>
                          <IconButton icon={<AttachmentIcon />} variant="outline" size="md" />
                        </Tooltip>
                        <Box position="absolute" right="-8px" top="-8px">
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            onClick={() => handleDeleteAttachment(index)}
                            aria-label="Remove attachment"
                            variant="ghost"
                            style={{
                              filter:
                                "invert(20%) sepia(50%) saturate(7500%) hue-rotate(356deg) brightness(100%) contrast(101%)",
                            }}
                          />
                        </Box>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </HStack>
            </HStack>

            <div style={{ width: "100%" }} id="input-box">
              <Flex direction="column" position="relative">
                <Flex direction="column" w="full" position="relative">
                  <div
                    contentEditable
                    ref={textareaRef}
                    className="chatareatext"
                    id="text-area"
                    name="question"
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onKeyUp={checkMention}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  {placeHolderVisible && (!draft || !isDraftBoxOpen) && (
                    <div onClick={handlePlaceHolderClick} style={{ position: "absolute", top: "0px", left: "0px" }}>
                      Type your message...
                    </div>
                  )}

                  <Flex align="center" className="text-area-button">
                    <Button variant="sendMessage" onClick={handleSubmit} isDisabled={disableSendButton} ml={"auto"} id="send-button">
                      <ArrowForwardIcon />
                    </Button>
                  </Flex>
                </Flex>
                <Box
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  style={{ position: "absolute", right: "55px", bottom: "-15px" }}
                  mt="32px"
                  mb="0px"
                >
                </Box>
              </Flex>
            </div>
          </VStack>
        </Flex>
      </Box>
    </>
  );
}

export default SendArea;
